import React, { useEffect, useState } from "react";
import { readQueue, subscribe } from "@/lib/offlineQueue";
import { NavLink, useNavigate } from "react-router-dom";
import { PieChart, Briefcase, Gavel, Building2, UserCheck, FileText, BookOpen, LogOut, ChevronLeft, Settings2, Menu, Search, BarChart3, Sun, LifeBuoy } from "lucide-react";
import { SimplyServedLogo } from "@/components/site/SimplyServedLogo";
import { useAuth } from "@/lib/auth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { GlobalSearch, useGlobalSearchHotkey } from "@/components/GlobalSearch";
import { OfflineSync } from "@/components/OfflineSync";
import { NotificationBell } from "@/components/NotificationBell";
import { GuidedTour } from "@/components/GuidedTour";

const nav = [
  { to: "/today", label: "My Day", icon: Sun, testId: "nav-today" },
  { to: "/dashboard", label: "Overview", icon: PieChart, testId: "nav-dashboard", admin: true },
  { to: "/jobs", label: "Jobs", icon: Briefcase, testId: "nav-jobs", serverLabel: "My Jobs" },
  { to: "/cases", label: "Court Cases", icon: Gavel, testId: "nav-cases", admin: true },
  { to: "/clients", label: "Companies", icon: Building2, testId: "nav-clients", admin: true },
  { to: "/servers", label: "Servers", icon: UserCheck, testId: "nav-servers", admin: true },
  { to: "/affidavits", label: "Affidavits", icon: FileText, testId: "nav-affidavits", admin: true },
  { to: "/forms", label: "Court Forms", icon: BookOpen, testId: "nav-forms", admin: true },
  { to: "/reports", label: "Reports", icon: BarChart3, testId: "nav-reports", admin: true },
  { to: "/settings", label: "Settings", icon: Settings2, testId: "nav-settings", serverLabel: "My Settings" },
  { to: "/help", label: "Partner Guide", icon: LifeBuoy, testId: "nav-help", admin: true },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  useGlobalSearchHotkey(setSearchOpen);
  const items = nav.filter((n) => !n.admin || user?.role === "admin").map((n) => (user?.role === "server" && n.serverLabel ? { ...n, label: n.serverLabel } : n));
  const [queued, setQueued] = useState(readQueue().length);
  useEffect(() => subscribe((q) => setQueued(q.length)), []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex flex-col md:flex-row h-[100dvh] overflow-hidden bg-[#f3f4f6] text-slate-900">
      <header className="md:hidden sm-sidebar flex items-center justify-between px-3 shrink-0 pt-[env(safe-area-inset-top)]" style={{ minHeight: "calc(3rem + env(safe-area-inset-top))" }} data-testid="mobile-header">
        <button type="button" data-testid="mobile-menu-button" onClick={() => setMobileOpen((o) => !o)} className="text-white p-1"><Menu className="h-5 w-5" /></button>
        <SimplyServedLogo className="h-8 w-auto" />
        <div className="flex items-center gap-1">
          <button type="button" data-testid="mobile-search-button" onClick={() => setSearchOpen(true)} className="text-white/80 p-1"><Search className="h-4 w-4" /></button>
          <NotificationBell />
          <button data-testid="mobile-logout-button" onClick={handleLogout} className="text-white/80 p-1"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>
      {mobileOpen && (
        <div className="md:hidden sm-sidebar border-t border-white/10 grid grid-cols-2 gap-1 p-2 shrink-0" data-testid="mobile-nav">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`m-${n.testId}`} onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `sm-nav-item rounded ${isActive ? "active" : ""}`}>
              <n.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={2} /><span>{n.label}</span>{n.to === "/today" && queued > 0 && <span className="ml-auto text-[10px] font-bold rounded-full bg-amber-400 text-amber-950 px-1.5 py-0.5" title={`${queued} attempt(s) saved offline, waiting to sync`} data-testid="nav-offline-badge">{queued}</span>}
            </NavLink>
          ))}
        </div>
      )}
      <aside className={`hidden md:flex ${collapsed ? "w-16" : "w-40"} shrink-0 sm-sidebar flex-col transition-[width] duration-200`} data-testid="sidebar">
        <div className="h-14 flex items-center justify-center border-b border-white/10 px-2">
          {collapsed ? <SimplyServedLogo withWordmark={false} className="h-9 w-auto" /> : <SimplyServedLogo className="h-11 w-auto" data-testid="brand-name" />}
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          <button type="button" data-testid="global-search-button" onClick={() => setSearchOpen(true)} title="Search (press /)"
            className={`sm-nav-item w-full ${collapsed ? "justify-center px-0" : ""}`}>
            <Search className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
            {!collapsed && <span className="flex-1 text-left">Search</span>}
            {!collapsed && <kbd className="text-[10px] text-white/60 border border-white/20 rounded px-1">/</kbd>}
          </button>
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testId} title={n.label}
              className={({ isActive }) => `sm-nav-item ${isActive ? "active" : ""} ${collapsed ? "justify-center px-0" : ""}`}>
              <n.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
              {!collapsed && <span>{n.label}</span>}
              {n.to === "/today" && queued > 0 && (collapsed ? <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-amber-400" data-testid="nav-offline-dot" /> : <span className="ml-auto text-[10px] font-bold rounded-full bg-amber-400 text-amber-950 px-1.5 py-0.5" title={`${queued} attempt(s) saved offline, waiting to sync`} data-testid="nav-offline-badge">{queued}</span>)}
            </NavLink>
          ))}
          <button type="button" data-testid="sidebar-collapse" onClick={() => setCollapsed((c) => !c)}
            className={`sm-nav-item w-full ${collapsed ? "justify-center px-0" : ""}`}>
            <ChevronLeft className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
          </button>
        </nav>
        <div className="p-2 border-t border-white/10">
          <div className={`flex items-center gap-2 ${collapsed ? "justify-center" : ""}`}>
            <Avatar className="h-8 w-8 border border-white/20">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-white/20 text-white text-xs">{(user?.name || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-white truncate" data-testid="user-name">{user?.name}</div>
                <div className="text-[10px] text-white/70 uppercase" data-testid="user-role-badge">{user?.role}</div>
              </div>
            )}
            <button data-testid="logout-button" onClick={handleLogout} title="Sign out" className="text-white/70 hover:text-white p-1">
              <LogOut className="h-4 w-4" />
            </button>
            <NotificationBell className="hidden md:block" />
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-y-auto flex flex-col pb-[env(safe-area-inset-bottom)]">
        <OfflineSync />
        <div className="flex-1">{children}</div>
      </main>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <GuidedTour />
    </div>
  );
}
